/**
 * Minimal 3D vector maths.
 *
 * Hand-rolled rather than pulled from three.js on purpose: this code runs on the
 * server too, and the client and server must execute *identical* arithmetic for
 * prediction to agree with authority. A dependency that the two sides could
 * resolve to different versions is a divergence waiting to happen.
 *
 * Everything is plain `{x, y, z}` — the same shape Rapier accepts as a Vector,
 * so no conversion happens at the physics boundary.
 *
 * `Math.hypot` is deliberately avoided: its precision is implementation-defined,
 * and `sqrt(dot(v, v))` is exactly reproducible.
 */
export interface Vec3 {
  x: number;
  y: number;
  z: number;
}

export function vec3(x = 0, y = 0, z = 0): Vec3 {
  return { x, y, z };
}

export function clone(v: Vec3): Vec3 {
  return { x: v.x, y: v.y, z: v.z };
}

export function add(a: Vec3, b: Vec3): Vec3 {
  return { x: a.x + b.x, y: a.y + b.y, z: a.z + b.z };
}

export function sub(a: Vec3, b: Vec3): Vec3 {
  return { x: a.x - b.x, y: a.y - b.y, z: a.z - b.z };
}

export function scale(v: Vec3, s: number): Vec3 {
  return { x: v.x * s, y: v.y * s, z: v.z * s };
}

export function addScaled(a: Vec3, b: Vec3, s: number): Vec3 {
  return { x: a.x + b.x * s, y: a.y + b.y * s, z: a.z + b.z * s };
}

export function dot(a: Vec3, b: Vec3): number {
  return a.x * b.x + a.y * b.y + a.z * b.z;
}

export function lengthSquared(v: Vec3): number {
  return dot(v, v);
}

export function length(v: Vec3): number {
  return Math.sqrt(dot(v, v));
}

export function distance(a: Vec3, b: Vec3): number {
  return length(sub(a, b));
}

/** Returns a zero vector for a zero-length input rather than NaN. */
export function normalize(v: Vec3): Vec3 {
  const len = length(v);
  return len > 0 ? scale(v, 1 / len) : vec3();
}

/** Removes the component of `v` along `normal` — the slide projection. */
export function projectOnPlane(v: Vec3, normal: Vec3): Vec3 {
  return addScaled(v, normal, -dot(v, normal));
}

export function isFinite3(v: Vec3): boolean {
  return Number.isFinite(v.x) && Number.isFinite(v.y) && Number.isFinite(v.z);
}
